// Forex currency pair flag emoji mapping
export const PAIR_FLAGS: Record<string, string> = {
  'EUR/USD': '🇪🇺🇺🇸',
  'USD/JPY': '🇺🇸🇯🇵',
  'GBP/USD': '🇬🇧🇺🇸',
  'AUD/USD': '🇦🇺🇺🇸',
  'USD/CAD': '🇺🇸🇨🇦',
  'NZD/USD': '🇳🇿🇺🇸',
  'USD/CHF': '🇺🇸🇨🇭',
};

export interface ForexPair {
  pair: string;
  price: number | null;
  priceDigits?: number;
  change: number;
  pips: number;
  changeUnitLabel?: string;
  spread: number;
  spreadUnitLabel?: string;
  flags: string;
  favorite?: boolean;
  history?: number[];
}

export function normalizeSymbol(symbol: string): string {
  if (symbol.includes('/')) return symbol;
  if (symbol.length === 6) return `${symbol.slice(0, 3)}/${symbol.slice(3)}`;
  return symbol;
}

export function compactSymbol(pair: string): string {
  return pair.replace('/', '');
}

export function getPairFlags(symbol: string): string {
  return PAIR_FLAGS[normalizeSymbol(symbol)] || '🌐';
}

export function toFinitePrice(value: string | number | null | undefined): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
}

export function midpointPrice(bid: string | number | null | undefined, ask: string | number | null | undefined): number | null {
  const bidPrice = toFinitePrice(bid);
  const askPrice = toFinitePrice(ask);
  if (bidPrice == null || askPrice == null) return null;
  return (bidPrice + askPrice) / 2;
}

// Format price based on pair
export function formatPrice(pair: string, price: number | null, digits?: number): string {
  if (price == null || !Number.isFinite(price)) return '--';
  if (digits != null && Number.isInteger(digits) && digits >= 0 && digits <= 10) return price.toFixed(digits);
  if (compactSymbol(pair) === 'XAUUSD') return price.toFixed(2);
  if (pair.includes('JPY')) {
    return price.toFixed(3);
  }
  return price.toFixed(5);
}

// Format pips with sign
export function formatPips(pips: number, unitLabel = 'pip'): string {
  if (!Number.isFinite(pips)) return '-- pips';
  const sign = pips >= 0 ? '+' : '';
  const unit = unitLabel === 'pip' ? 'pips' : unitLabel === 'point' ? 'points' : `× ${unitLabel}`;
  return `${sign}${pips.toFixed(1)} ${unit}`;
}

// Format change percent
export function formatChange(change: number): string {
  if (!Number.isFinite(change)) return '--';
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(2)}%`;
}

// Is London-NY overlap active
export function isLondonNYOverlap(): boolean {
  const now = new Date();
  const hour = now.getUTCHours();
  return hour >= 12 && hour < 17;
}

// Get current trading session
export function getTradingSession(): string {
  const now = new Date();
  const hour = now.getUTCHours();
  if (hour >= 0 && hour < 8) return 'Sydney-Tokyo';
  if (hour >= 8 && hour < 12) return 'London';
  if (hour >= 12 && hour < 17) return 'London-NY';
  return 'New York';
}
